/**
 * The email rules that BOTH the browser and the server have to agree about.
 *
 * WHY THIS FILE EXISTS SEPARATELY FROM `campaigns.ts`
 *
 * The composer decides who an email is for. The sender — a Cloudflare Function
 * holding the service key — decides it again, from scratch, because a browser's
 * word is not something a thing that can email 785 people should take. Two
 * independent answers to "who", and the moment they disagree somebody gets an
 * email they asked not to receive.
 *
 * The obvious fix is for the Function to import `campaigns.ts`. The obvious fix
 * has a catch: `campaigns.ts` pulls in `address.ts` for name tidying, which is
 * eleven kilobytes of postal abbreviations the server has no use for, and every
 * import a Function carries is another thing that can fail to bundle in an
 * environment nobody can run locally.
 *
 * So the shared part lives here, on its own, with **no imports at all**. It is
 * the leaf of the dependency graph deliberately. `campaigns.ts` re-exports from
 * it, the Function imports it directly, and `test-emailrules.mjs` asserts the
 * two see the same thing — because "we copied it and we'll keep them in sync"
 * is not a mechanism.
 */

export type Lane = 'reviewed' | 'blast'

/** Where an email leaves from. The lane decides this; nobody picks it. */
export const LANE_FROM: Record<Lane, string> = {
  reviewed: 'renee.roark@therealbrokerage.com',
  blast: 'renee@reneeroark.com',
}

/** Replies always come back to the inbox she actually reads. */
export const REPLY_TO = 'renee.roark@therealbrokerage.com'

/**
 * Tags that stop an email. `no-outreach` covers the milestone lane only, so
 * Renee can silence her own birthday card and still receive every broadcast —
 * which is deliberate, because receiving the team-call reminder is how she
 * knows it went out.
 */
export const SUPPRESSION: Record<Lane, string[]> = {
  reviewed: ['no-outreach'],
  blast: ['no-bulk-email'],
}

/**
 * What the two suppression tags are called on screen, and what each one still
 * lets through.
 *
 * The tag names are the thing anybody reads on an agent card, and they do not
 * say what they do. "no-outreach" sounds like *no outreach* — total silence —
 * and it is not: that agent still receives every mass email. "no-bulk-email"
 * sounds like it might cover the lot, and it leaves birthdays and anniversaries
 * running. Somebody acting on the name alone leaves the wrong half switched on,
 * and nothing on the card corrects them.
 *
 * The labels live here rather than in the panel because this file is where the
 * rule itself lives. Two copies of "which tag stops which lane" — one in the
 * rule and one in the wording — is exactly the pair that drifts, and the wording
 * is the copy nobody re-checks.
 *
 * Chris's decision, 2026-08-17: keep both tags and name them properly. A single
 * master switch was rejected because the case that actually turns up is somebody
 * who will take a birthday note but not marketing; per-trigger tick boxes were
 * rejected as more control than anyone asked for.
 *
 * NOT a complete list of `no-…-email` tags. The per-trigger ones
 * (`no-birthday-email` and friends) are read by the milestone drafting, not by
 * the composer, so they are deliberately absent — labelling them here would
 * claim the composer honours them, and it does not.
 */
export type SuppressionTag = (typeof SUPPRESSION)[Lane][number]

/*
  Typed against SUPPRESSION rather than `Record<string, …>`.

  The comment above says two copies of "which tag stops which lane" is the pair
  that drifts, and then the first draft of this built exactly that: a plain
  string map that would silently have no label for a tag added to SUPPRESSION,
  and silently no effect for a key with a typo in it. Keyed off SUPPRESSION's
  own values, both of those stop compiling. `test-emailrules.mjs` asserts the
  coverage as well, because a type only holds inside this repo's build.
*/
export const SUPPRESSION_LABELS: Record<SuppressionTag, { label: string; allows: string }> = {
  'no-outreach': {
    label: 'No one-to-one emails',
    allows: 'Left out of birthdays, anniversaries and anything read and sent one at a time. Still receives mass emails.',
  },
  'no-bulk-email': {
    label: 'No mass emails',
    allows: 'Left out of anything sent to everybody at once. Still receives one-to-one emails like a birthday note.',
  },
}

/**
 * The label for a tag as it is actually stored, or null if it is not one of the
 * two that stop email.
 *
 * Normalised, because `suppressedBy` below normalises and this has to agree
 * with it. A tag written as `No-Outreach` or with a trailing space — an import,
 * a migration, somebody's hand-written SQL; only tags added through the tag box
 * go through `normaliseTag` — suppresses that agent from every reviewed send,
 * and an exact-match lookup would render it as a plain grey chip with no
 * explanation at all. The card that exists to say "this person is being left
 * out, and here is what they still get" would say nothing, on precisely the
 * agent it matters for.
 */
export function suppressionLabel(tag: string): { label: string; allows: string } | null {
  return SUPPRESSION_LABELS[tag.trim().toLowerCase() as SuppressionTag] ?? null
}

/**
 * True when this agent is out of BOTH lanes.
 *
 * Each `allows` sentence is only true on its own. Show them together — which is
 * how "do not email me at all" is recorded — and they contradict each other:
 * one says still receives mass emails, the other says still receives one-to-one
 * emails, and in fact this person receives nothing. The panel needs to say that
 * instead of both halves.
 */
export function silencedEntirely(tags: readonly string[]): boolean {
  const set = new Set(tags.map((t) => t.trim().toLowerCase()))
  return Object.keys(SUPPRESSION_LABELS).every((t) => set.has(t))
}

const EMAIL_OK = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/

/**
 * The address an agent is written to. Their own, never the `@joinreal.com` one.
 *
 * Typed structurally rather than against `Agent`, so the server can pass a raw
 * PostgREST row without importing the app's types.
 */
export function addressFor(a: { email?: string | null } | null | undefined): string | null {
  const e = (a?.email ?? '').trim()
  return e && EMAIL_OK.test(e) ? e : null
}

/** Does this agent hold a tag that stops this kind of email? Returns which one. */
export function suppressedBy(tags: readonly string[], lane: Lane): string | null {
  const stops = SUPPRESSION[lane]
  return (
    tags
      .map((t) => t.trim().toLowerCase())
      .find((t) => stops.includes(t)) ?? null
  )
}

/** Every `{{ thing }}` in a piece of text, in the order they appear. */
export function mergeFieldsUsed(text: string): string[] {
  const found = [...text.matchAll(/\{\{\s*([\w.]+)\s*\}\}/g)].map((m) => m[1])
  return [...new Set(found)]
}

/**
 * The rule the whole design rests on, in one function both sides can call.
 *
 * A blast is read once by a human and then goes to everybody unread, so there
 * is nobody to catch a name that came through wrong — and 21 first names in
 * this roster are ALL CAPS or all lowercase because agents type them into
 * reZEN themselves.
 *
 * The composer refuses to queue one. The sender refuses to send one. The second
 * check is not redundant: the row sits in a table between those two moments,
 * and anything with a service key could have edited it.
 */
export function blastMayNotMerge(subject: string, body: string): string[] {
  return mergeFieldsUsed(`${subject}\n${body}`)
}

// ------------------------------------------------------------------ signature

/**
 * Renee's HiHello digital business card, on bulk (blast-lane) emails only.
 *
 * Raised 2026-08-23 night: "How do we add her email signature to the bulk
 * emails that send from Resend? She has a Hi Hello Card." Scoped to the blast
 * lane deliberately — the reviewed lane leaves through her own Gmail, which
 * already appends her Gmail signature before she presses send (see
 * `guide.ts`, `Outbox.tsx`: "Add your signature in Gmail before sending").
 * The blast lane is server-to-Resend and has never had any signature at all.
 * Full design: `claude/bulk-email-signature-design-2026-08-23.md`.
 *
 * Lives here, not in `campaigns.ts`, for the same reason everything else in
 * this file does: `send-campaign.ts` is a Cloudflare Function and needs this
 * without pulling in anything that does not bundle cleanly there.
 */

/**
 * The exact embed HiHello exports for her card, pasted in by Chris
 * 2026-08-23 and kept byte-for-byte rather than "cleaned up" — the
 * `?generated=…` query string is HiHello's own cache-busting stamp, and the
 * bare `noreferrer` attribute (parsed as `noreferrer="noreferrer"` by every
 * real renderer) is exactly what HiHello's own export tool wrote. Changing
 * either risks it no longer matching what HiHello's own analytics/click
 * tracking expects for this card.
 */
export const SIGNATURE_IMAGE_HTML =
  '<a href="https://hihello.com/p/cb092467-5a09-48a8-a67d-e62c830fb211?referer=email_signature&source=manual" ' +
  'rel="noopener" noreferrer target="_blank" style="display: inline-block">' +
  '<img alt="This is Renee  Roark\'s card. Their email is renee.roark@onereal.com. Their phone number is +1 803 460 4373." ' +
  'src="https://cdn.hihello.me/cards/cb092467-5a09-48a8-a67d-e62c830fb211/signature_imagelogo.png?generated=1787525493603" ' +
  'width="360" style="display: inline-block; min-height: 100px" /></a>'

/**
 * The card's own public profile link, without the `signature`-tracking
 * query params the image embed carries — used as plain, readable text
 * rather than hidden behind an `<img alt>` nobody sees unless the picture
 * fails to load.
 */
export const SIGNATURE_CARD_URL = 'https://hihello.com/p/cb092467-5a09-48a8-a67d-e62c830fb211'

/**
 * Confirmed by Chris, 2026-08-23: "That is the correct email. Shorter
 * version that forwards to her formal email." Not the technical send-from
 * address (`LANE_FROM.blast`) or the reply-to (`REPLY_TO`) — a card is
 * allowed to show a different public-facing address, and this one already
 * forwards to her real mailbox on its own. Not reconciled with the other two
 * on purpose.
 */
const SIGNATURE_EMAIL = 'renee.roark@onereal.com'
const SIGNATURE_PHONE = '(803) 460-4373'
const SIGNATURE_NAME = 'Renee Roark'

/**
 * The always-visible line, decided after Chris: "Add the plain text
 * option." The HiHello embed above is image-only and carries nothing a
 * recipient can read if the image itself doesn't load — a corporate inbox
 * blocking remote images by default is ordinary, not exotic. Same
 * information the image's own `alt` text already carries (name, email,
 * phone), plus the card link, just readable without the picture.
 *
 * This is also the ENTIRE signature for the plain-`text` fallback Resend
 * sends alongside the HTML — plain text cannot carry an image at all.
 */
export const SIGNATURE_TEXT_LINE = `${SIGNATURE_NAME}  |  ${SIGNATURE_EMAIL}  |  ${SIGNATURE_PHONE}  |  ${SIGNATURE_CARD_URL}`

/** Exported so callers building their own HTML fragment around a blast (the
 *  test-send preamble in `send-campaign.ts`, naming the campaign) can escape
 *  agent-typed text the same way this file does, rather than growing a
 *  second copy of the same three replacements. */
export const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/**
 * The full HTML signature block: the card image, then the plain-text line
 * underneath it as real, always-rendered HTML (not an `alt` attribute) —
 * so it reads whether or not the image itself loads.
 */
export function signatureHtml(): string {
  const line =
    `${escapeHtml(SIGNATURE_NAME)} &nbsp;|&nbsp; ` +
    `<a href="mailto:${SIGNATURE_EMAIL}" style="color:#444">${SIGNATURE_EMAIL}</a> &nbsp;|&nbsp; ` +
    `${escapeHtml(SIGNATURE_PHONE)} &nbsp;|&nbsp; ` +
    `<a href="${SIGNATURE_CARD_URL}" rel="noopener" target="_blank" style="color:#444">View my digital card</a>`
  return (
    `<div style="margin-top:24px">${SIGNATURE_IMAGE_HTML}` +
    `<div style="margin-top:8px;font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;font-size:13px;line-height:1.4;color:#555">${line}</div>` +
    `</div>`
  )
}

/**
 * A plain-text blast body, escaped into HTML paragraphs.
 *
 * The composer is an ordinary textarea — Renee types plain text, blank lines
 * between paragraphs, no markup — and that stays true after this. Blank-line
 * runs become paragraph breaks; a single newline inside a paragraph becomes a
 * `<br>` rather than being collapsed, so a short list she typed on separate
 * lines still reads as separate lines in an HTML client.
 *
 * LINE ENDINGS ARE NORMALISED FIRST, AND THAT IS NOT OPTIONAL.
 *
 * Caught by a second adversarial review pass: `\n{2,}` never matches inside a
 * `\r\n\r\n` blank line, because the two `\n` characters are not adjacent —
 * each is separated by its own `\r`. A body that ever entered the system
 * with Windows-style line endings (a template row loaded from storage rather
 * than typed live into this session's textarea, a hand-edited SQL row, an
 * import) would collapse into ONE paragraph with a stray `<br><br>` where the
 * break should be, and un-stripped `\r` characters sitting inside the
 * markup — silently, since nothing downstream previews the actual HTML
 * before it goes (`Bulk.tsx`'s own body preview renders the raw text, not
 * `blastHtmlBody`'s output). `\r\n` → `\n` and a stray lone `\r` (old
 * classic-Mac line endings, same reasoning) → `\n` both fold before anything
 * else runs, so every downstream rule only ever has to reason about `\n`.
 */
/**
 * Trailing punctuation a sentence puts right after a URL and that a reader
 * would not expect to be part of the link — "see https://x.com." should not
 * eat the period into the href.
 */
const URL_TRAILING_PUNCTUATION = /[.,;:!?)\]]+$/

/**
 * Wrap a bare `http(s)://` URL in a real anchor, matching what Gmail already
 * does the instant a reviewed-lane draft opens with one typed in plain text.
 *
 * This is the one thing `blastHtmlBody` did NOT already match between the two
 * lanes: a bare URL typed into a template used for a reviewed send comes out
 * clickable for free, because Gmail's own compose window autolinks it — but
 * the identical text sent as a blast used to sit dead in the HTML, visible
 * and not clickable, with nothing about the source text explaining why one
 * lane worked and the other didn't.
 *
 * Runs on the ALREADY-ESCAPED paragraph, not the raw one. Escaping first and
 * matching the escaped form is what keeps a `&` inside a query string (an
 * ordinary thing for a URL to contain) from landing unescaped in attribute
 * position — matching the raw text and escaping each piece separately would
 * have to get that right in two places instead of relying on the one rule
 * `escapeHtml` already enforces everywhere else in this file.
 */
function linkifyUrls(escaped: string): string {
  return escaped.replace(/https?:\/\/[^\s<>]+/g, (match) => {
    const trail = match.match(URL_TRAILING_PUNCTUATION)?.[0] ?? ''
    const url = trail ? match.slice(0, -trail.length) : match
    if (!url) return match
    return `<a href="${url}" rel="noopener" target="_blank">${url}</a>${trail}`
  })
}

export function textToHtmlParagraphs(body: string): string {
  return body
    .replace(/\r\n?/g, '\n')
    .split(/\n{2,}/)
    .map((para) => `<p>${linkifyUrls(escapeHtml(para)).replace(/\n/g, '<br>')}</p>`)
    .join('\n')
}

/**
 * The "this inbox isn't monitored" line at the very bottom of every blast
 * (Chris, 2026-10-26). Replies already go to `REPLY_TO` via the reply-to
 * header; this covers people who copy `renee@reneeroark.com` into a NEW
 * email, which lands in a mailbox she doesn't read. Built from the same two
 * constants the send path uses, so the note can never name a different
 * address than the one the email actually comes from or replies to.
 */
export const UNMONITORED_NOTE =
  `Please note: ${LANE_FROM.blast} is not monitored. ` +
  `To reach me, email ${REPLY_TO} or simply reply to this message.`

export function unmonitoredNoteHtml(): string {
  return (
    `<p style="margin-top:20px;font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;font-size:12px;line-height:1.4;color:#777">` +
    `Please note: ${escapeHtml(LANE_FROM.blast)} is not monitored. To reach me, email ` +
    `<a href="mailto:${REPLY_TO}" style="color:#555">${escapeHtml(REPLY_TO)}</a> or simply reply to this message.</p>`
  )
}

/** The full HTML a blast should send: the body, the signature, then the unmonitored note. */
export function blastHtmlBody(body: string): string {
  return `${textToHtmlParagraphs(body)}\n${signatureHtml()}\n${unmonitoredNoteHtml()}`
}

/** The full plain-text a blast should send: the body, the signature line, then the unmonitored note. */
export function blastTextBody(body: string): string {
  return `${body}\n\n--\n${SIGNATURE_TEXT_LINE}\n\n${UNMONITORED_NOTE}`
}
